import { TZDate } from '@date-fns/tz'
import { addDays } from 'date-fns/addDays'
import { intlFormatDistance } from 'date-fns/intlFormatDistance'
import { intervalToDuration } from 'date-fns/intervalToDuration'
import { nextFriday } from 'date-fns/nextFriday'
import { nextMonday } from 'date-fns/nextMonday'
import { nextSaturday } from 'date-fns/nextSaturday'
import { nextSunday } from 'date-fns/nextSunday'
import { nextThursday } from 'date-fns/nextThursday'
import { nextTuesday } from 'date-fns/nextTuesday'
import { nextWednesday } from 'date-fns/nextWednesday'
import { set } from 'date-fns/set'
import { isBefore } from 'date-fns/isBefore'
import { isAfter } from 'date-fns/isAfter'
import { compareAsc } from 'date-fns/compareAsc'
import { entries } from 'remeda'

type EventDate = {
	start: Date
	end: Date
}

export const getEventInfo = (event: {
	timezoneOverride?: string | null
	occurrences: ({
		_type: 'cadencedOccurrence'
		days: {
			sunday: boolean
			monday: boolean
			tuesday: boolean
			wednesday: boolean
			thursday: boolean
			friday: boolean
			saturday: boolean
		}
		start: {
			hour: number
			minute: number
			second: number
		}
		end: {
			hour: number
			minute: number
			second: number
		}
	} | {
		_type: 'datedOccurrence'
		startDate: Date
		endDate: Date
	})[]
}, scheduleTimezone: string) => {
	const timezone = event.timezoneOverride ?? scheduleTimezone
	const now = getNow(timezone)

	const createEventDates = (occurrences: ({
		_type: 'cadencedOccurrence'
		days: {
			sunday: boolean
			monday: boolean
			tuesday: boolean
			wednesday: boolean
			thursday: boolean
			friday: boolean
			saturday: boolean
		}
		start: {
			hour: number
			minute: number
			second: number
		}
		end: {
			hour: number
			minute: number
			second: number
		}
	} | {
		_type: 'datedOccurrence'
		startDate: Date
		endDate: Date
	})[]) => {
		const weekInDays = getNextWeekInDays()

		const dates: EventDate[] = []

		for (const occurrence of occurrences) {
			switch (occurrence._type) {
			case 'cadencedOccurrence':
				for (const [
					day,
					enabled,
				] of entries(occurrence.days)) {
					const date = weekInDays[day]

					if (enabled) {
						const start = set(date, {
							hours: occurrence.start.hour,
							minutes: occurrence.start.minute,
							seconds: occurrence.start.second,
						})

						const end = set(date, {
							hours: occurrence.end.hour,
							minutes: occurrence.end.minute,
							seconds: occurrence.end.second,
						})

						dates.push({
							start,
							end: occurrence.start.hour > occurrence.end.hour ? addDays(end, 1) : end,
						})
					}
				}

				break
			case 'datedOccurrence':
				dates.push({
					start: occurrence.startDate,
					end: occurrence.endDate,
				})
			}
		}

		return dates.toSorted((a, b) => {
			return compareAsc(a.start, b.start)
		})
	}

	const getNextWeekInDays = () => {
		return {
			sunday: nextSunday(now),
			monday: nextMonday(now),
			tuesday: nextTuesday(now),
			wednesday: nextWednesday(now),
			thursday: nextThursday(now),
			friday: nextFriday(now),
			saturday: nextSaturday(now),
		}
	}

	const getHumanReadableCountdown = (nextEvent: Date | null) => {
		return intlFormatDistance(nextEvent || now, now)
	}

	const formatCountdown = (nextEvent: Date | null) => {
		const duration = intervalToDuration({
			start: now,
			end: nextEvent || now,
		})

		const pieces = [
			duration.days || 0,
			duration.hours || 0,
			duration.minutes || 0,
			duration.seconds || 0,
		].reduce<number[]>((pieces, piece) => {
			if (piece > 0 || piece === 0 && pieces.length > 0) {
				pieces.push(piece)
			}

			return pieces
		}, []).map((piece, index) => {
			if (index === 0) {
				return String(piece)
			}

			return String(piece).padStart(2, '0')
		})

		return pieces.join(':')
	}

	const dates = createEventDates(event.occurrences)

	const isOpen = () => {
		for (const date of dates) {
			if (isAfter(now, date.start) && isBefore(now, date.end)) {
				return true
			}
		}

		return false
	}

	const getCurrentEvent = () => {
		for (const date of dates) {
			if (isAfter(now, date.start) && isBefore(now, date.end)) {
				return date
			}
		}

		return null
	}

	const getNextEvent = () => {
		for (const date of dates) {
			if (isBefore(now, date.start)) {
				return date.start
			}

			if (isAfter(now, date.start) && isBefore(now, date.end)) {
				return date.end
			}
		}

		return null
	}

	const getState = () => {
		const open = isOpen()
		const nextEvent = getNextEvent()

		return {
			nextState: !open,
			countdown: formatCountdown(nextEvent),
			humanReadableCountdown: getHumanReadableCountdown(nextEvent),
			currentState: open,
			nextEvent,
		}
	}

	return {
		dates,
		isOpen: isOpen(),
		currentEvent: getCurrentEvent(),
		nextEvent: getNextEvent(),
		state: getState(),
	}
}

const getNow = (timezone: string) => {
	return TZDate.tz(timezone)
}
